/**
 * Segment Item Component
 * Clean, professional segment row in the timeline
 */

import React, { useState } from 'react';
import { MapPin, Plane, AlertTriangle } from 'lucide-react';
import {
  PlanningSegment,
  ANCHOR_CONFIGS,
  VEHICLE_CONFIGS,
  TRIP_TYPE_CONFIGS,
  AnchorType,
  VehicleType,
  TripType
} from '../../types/matchdayPlanning';
import VehicleSelectionModal from './VehicleSelectionModal';

interface SegmentItemProps {
  segment: PlanningSegment;
  planningId: number;
  isReadOnly?: boolean;
  gapMinutes?: number | null;
  onUpdate: (updates: Partial<PlanningSegment>) => void;
  onDelete: () => void;
  onEdit: () => void;
  onSaveFavorite?: (segmentId: number, planningId: number) => void;
}

const formatGapInline = (minutes: number): string => {
  if (minutes < 0) return `−${formatGapInline(Math.abs(minutes))}`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h > 0 && m > 0) return `${h}h${m}'`;
  if (h > 0) return `${h}h`;
  return `${m}'`;
};

const SegmentItem: React.FC<SegmentItemProps> = ({
  segment,
  planningId,
  isReadOnly = false,
  gapMinutes,
  onUpdate,
  onDelete,
  onEdit,
  onSaveFavorite,
}) => {
  const [isVehicleModalOpen, setIsVehicleModalOpen] = useState(false);
  // Normalize time from backend "HH:MM:SS" to "HH:MM" for <input type="time">
  const normalizeTime = (t: string | undefined) => (t || '').substring(0, 5);

  const [editValues, setEditValues] = useState({
    scheduledDate: segment.scheduledDate || '',
    scheduledTime: normalizeTime(segment.scheduledTime),
    stopoverDurationMinutes: segment.stopoverDurationMinutes || 30
  });

  // Update editValues when segment props change (e.g., after swap)
  React.useEffect(() => {
    setEditValues({
      scheduledDate: segment.scheduledDate || '',
      scheduledTime: normalizeTime(segment.scheduledTime),
      stopoverDurationMinutes: segment.stopoverDurationMinutes || 30
    });
  }, [segment.scheduledDate, segment.scheduledTime, segment.stopoverDurationMinutes]);

  const anchorConfig = ANCHOR_CONFIGS[segment.anchorType?.toLowerCase() as AnchorType] || ANCHOR_CONFIGS.free;
  const vehicleConfig = VEHICLE_CONFIGS[segment.vehicleType?.toLowerCase() as VehicleType] || VEHICLE_CONFIGS.bus;
  const tripConfig = TRIP_TYPE_CONFIGS[segment.tripType?.toLowerCase() as TripType] || TRIP_TYPE_CONFIGS.team;

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const newDate = e.target.value;
      if (newDate && !/^\d{4}-\d{2}-\d{2}$/.test(newDate)) {
        console.warn('Invalid date format:', newDate);
        return;
      }
      setEditValues(prev => ({ ...prev, scheduledDate: newDate }));
      onUpdate({ scheduledDate: newDate });
    } catch (error) {
      console.error('Error updating date:', error);
    }
  };

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const newTime = e.target.value;
      if (newTime && !/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(newTime)) {
        console.warn('Invalid time format:', newTime);
        return;
      }
      setEditValues(prev => ({ ...prev, scheduledTime: newTime }));
      onUpdate({ scheduledTime: newTime });
    } catch (error) {
      console.error('Error updating time:', error);
    }
  };


  const handleVehicleTypeChange = (vehicleType: VehicleType) => {
    onUpdate({ vehicleType });
  };

  const handleTripTypeToggle = () => {
    const newTripType: TripType = segment.tripType === 'team' ? 'empty' : 'team';
    onUpdate({ tripType: newTripType });
  };

  // Get driver name from vehicle assignment
  const getDriverName = (): string | null => {
    const va = segment.vehicleAssignment;
    if (!va) return null;
    if (va.traveler) return va.traveler.fullName;
    if (va.partnerDriver) return va.partnerDriver.fullName;
    return null;
  };

  const driverName = getDriverName();

  return (
    <div
      className={`segment-item segment-item--${segment.anchorType}`}
      data-anchor-type={segment.anchorType}
      data-vehicle={segment.vehicleType}
    >
      {/* 1. Anchor Type */}
      <div className="segment-anchor">
        <span className={`segment-type-badge segment-type-badge--${segment.anchorType}`}>
          {anchorConfig.name}
        </span>
      </div>

      {/* 2. Date */}
      <div className="segment-date">
        <input
          type="date"
          className="date-input"
          value={editValues.scheduledDate}
          onChange={handleDateChange}
          disabled={isReadOnly || segment.anchorType === 'main'}
          title={segment.anchorType === 'main' ? 'Anstoß-Datum (wird über Planung gesteuert)' : 'Datum'}
        />
      </div>

      {/* 3. Time */}
      <div className="segment-time-col">
        <input
          type="time"
          className="time-input"
          value={editValues.scheduledTime}
          onChange={handleTimeChange}
          disabled={isReadOnly || segment.anchorType === 'main'}
          title={segment.anchorType === 'main' ? 'Anstoß (fest)' : 'Zeit'}
        />
      </div>

      {/* 4. Location / Title */}
      <div className="segment-location">
        <span className="location-name">
          {segment.vehicleType === 'plane' && segment.location ? (
            <><Plane size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Flug {segment.location}</>
          ) : (
            segment.location || '–'
          )}
        </span>
        {segment.description && (
          <span className="location-description"> – {segment.description}</span>
        )}
        {segment.customLocation && (
          <span className="location-address" style={{ display: 'block', fontSize: '0.75rem', color: '#6c757d', marginTop: '2px' }}>
            <MapPin size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 2 }} /> {segment.customLocation}
          </span>
        )}
        {segment.anchorType !== 'stopover' && segment.anchorType !== 'main' && (segment.estimatedDistanceKm ?? 0) > 0 && (
          <span style={{ display: 'block', fontSize: '0.75rem', marginTop: '2px' }}>
            {segment.co2eKg != null ? (
              <span style={{ color: '#16a34a' }}>
                {segment.co2eKg.toLocaleString('de-DE', { maximumFractionDigits: 1 })} kg CO₂e
                {segment.passengerCount ? ` · ${segment.passengerCount} Pax` : ''}
              </span>
            ) : (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: '#d97706' }}>
                <AlertTriangle size={12} /> CO₂e-Faktor fehlt
              </span>
            )}
          </span>
        )}
      </div>

      {/* 4b. Gap time (duration since previous segment) */}
      <div className="segment-gap-time" style={{ textAlign: 'right', fontSize: '0.75rem', color: '#6c757d', fontWeight: 500, whiteSpace: 'nowrap' }}>
        {gapMinutes != null ? formatGapInline(gapMinutes) : ''}
      </div>

      {/* 5. Vehicle + Trip Type combined (hidden for stopover/main) */}
      <div className="segment-vehicle-combined">
        {segment.anchorType !== 'stopover' && segment.anchorType !== 'main' ? (
          <>
            <button
              className={`vehicle-btn vehicle-btn--${segment.vehicleType}`}
              onClick={() => !isReadOnly && setIsVehicleModalOpen(true)}
              disabled={isReadOnly}
              title="Fahrzeug wählen"
            >
              {vehicleConfig.name}
            </button>
            <button
              className={`trip-type-btn trip-type-btn--${segment.tripType}`}
              onClick={handleTripTypeToggle}
              disabled={isReadOnly}
              title={segment.tripType === 'team' ? 'Teamfahrt' : 'Leerfahrt'}
            >
              {segment.tripType === 'team' ? 'T' : 'L'}
            </button>
          </>
        ) : (
          <span className="driver-empty">–</span>
        )}
      </div>

      {/* 6. Driver Name (hidden for stopover/main) */}
      <div className="segment-driver">
        {segment.anchorType !== 'stopover' && segment.anchorType !== 'main' ? (
          driverName ? (
            <span className="driver-name">{driverName}</span>
          ) : (
            <span className="driver-empty">–</span>
          )
        ) : (
          <span className="driver-empty">–</span>
        )}
      </div>

      {/* 7. Actions */}
      <div className="segment-actions">
        {!isReadOnly && (
          <>
            {segment.anchorType !== 'main' && onSaveFavorite && (
              <button
                className="action-btn action-btn--favorite"
                onClick={() => onSaveFavorite(segment.id, planningId)}
                title="Als Favorit speichern"
              >⭐</button>
            )}
            <button className="action-btn action-btn--edit" onClick={onEdit} title="Bearbeiten">✎</button>
            <button
              className="action-btn action-btn--delete"
              onClick={onDelete}
              disabled={segment.anchorType === 'main'}
              title={segment.anchorType === 'main' ? 'Hauptanker kann nicht gelöscht werden' : 'Löschen'}
            >×</button>
          </>
        )}
      </div>

      {/* Vehicle Selection Modal */}
      {isVehicleModalOpen && (
        <VehicleSelectionModal
          currentVehicle={segment.vehicleType}
          onSelect={handleVehicleTypeChange}
          onClose={() => setIsVehicleModalOpen(false)}
        />
      )}
    </div>
  );
};

export default SegmentItem;
